// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronsUpDown, Plus, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRippleDelegated } from "@/hooks/use-ripple";
import { cn } from "@/lib/utils";

const ITEM_HEIGHT = 36;

interface VirtualSelectOption {
  label: string;
  value: string;
}

interface VirtualSelectPopoverProps {
  value: string;
  onChange: (v: string) => void;
  options: readonly VirtualSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  /** Allow committing the typed search text as a free-form value not present in `options`. */
  allowCustom?: boolean;
  /** Show a clear (✕) button on the trigger when a value is set, resetting it to `""`. */
  clearable?: boolean;
}

export function VirtualSelectPopover({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  className,
  loading,
  disabled,
  allowCustom,
  clearable,
}: VirtualSelectPopoverProps) {
  const { t } = useTranslation("back-office");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);

  useRippleDelegated(scrollEl);

  useEffect(
    function scrollToTopOnOpen() {
      if (open && scrollEl) scrollEl.scrollTop = 0;
    },
    [open, scrollEl],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const base = q === "" ? options : options.filter((o) => o.label.toLowerCase().includes(q));
    // When not searching and a value is selected, pin it to top
    if (q === "" && value) {
      const selectedIdx = base.findIndex((o) => o.value === value);
      if (selectedIdx > 0) {
        return [base[selectedIdx], ...base.slice(0, selectedIdx), ...base.slice(selectedIdx + 1)] as typeof base;
      }
    }
    return base;
  }, [options, search, value]);

  useEffect(function resetFocusIndexOnMount() {
    setFocusedIndex(-1);
  }, []);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 8,
  });

  // Free-form ("create") affordance: offered when allowCustom is set, the user typed something,
  // and it doesn't already match an existing option's label.
  const trimmedSearch = search.trim();
  const showCreate =
    !!allowCustom &&
    trimmedSearch !== "" &&
    !options.some((o) => o.label.toLowerCase() === trimmedSearch.toLowerCase());

  function select(option: VirtualSelectOption) {
    onChange(option.value);
    setOpen(false);
  }

  function createCustom() {
    onChange(trimmedSearch);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(focusedIndex + 1, filtered.length - 1);
      setFocusedIndex(next);
      virtualizer.scrollToIndex(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(focusedIndex - 1, 0);
      setFocusedIndex(prev);
      virtualizer.scrollToIndex(prev);
    } else if (e.key === "Enter") {
      const item = focusedIndex >= 0 ? filtered[focusedIndex] : undefined;
      if (item) {
        e.preventDefault();
        select(item);
      } else if (showCreate) {
        e.preventDefault();
        createCustom();
      }
    }
  }

  // Fall back to the raw value for custom entries that aren't among the options
  const selectedLabel = options.find((o) => o.value === value)?.label ?? (allowCustom && value ? value : undefined);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTimeout(() => setSearch(""), 150);
        if (o) setFocusedIndex(-1);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={loading || disabled}
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className={`truncate ${selectedLabel && !loading ? "" : "text-muted-foreground"}`}>
            {loading ? t("common.loading") : (selectedLabel ?? placeholder)}
          </span>
          {clearable && value && !loading && !disabled ? (
            // A span (not a nested <button>) so it can live inside the trigger Button; the pointer-down
            // guard stops the popover from toggling before onClick clears the value.
            <span
              role="button"
              tabIndex={-1}
              aria-label={t("common.clear")}
              className="ml-2 shrink-0 rounded-sm opacity-50 hover:opacity-100"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }}
            >
              <X className="h-4 w-4" />
            </span>
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        side="bottom"
        sideOffset={-36}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        {showCreate && (
          <button
            type="button"
            className="flex w-full items-center gap-2 border-b px-2 text-sm hover:bg-accent"
            style={{ height: ITEM_HEIGHT }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={createCustom}
          >
            <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 truncate text-left">{t("common.useValue", { value: trimmedSearch })}</span>
          </button>
        )}
        <div
          ref={setScrollEl}
          className="overflow-y-auto rounded-b-md"
          style={{ height: Math.min(filtered.length * ITEM_HEIGHT, 220) }}
          onWheel={(e) => e.stopPropagation()}
        >
          {filtered.length === 0 ? (
            showCreate ? null : (
              <div className="py-4 text-center text-sm text-muted-foreground">{emptyText}</div>
            )
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((vItem) => {
                const option = filtered[vItem.index];
                if (!option) return null;
                const isSelected = option.value === value;
                const isFocused = vItem.index === focusedIndex;
                const isPinnedSelected = isSelected && vItem.index === 0 && !search;
                return (
                  <div
                    key={option.value}
                    data-ripple
                    style={{ position: "absolute", top: vItem.start, left: 0, right: 0, height: ITEM_HEIGHT }}
                    className={cn(
                      "relative overflow-hidden flex items-center gap-2 px-2 cursor-pointer text-sm hover:bg-accent",
                      isFocused ? "bg-accent" : isSelected ? "bg-accent/60" : "",
                      isPinnedSelected && "border-b",
                    )}
                    onClick={() => select(option)}
                  >
                    <span className="flex-1 truncate">{option.label}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
